import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ShoppingBag, Plus, Check, X, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UPSELL_TEXT = {
  en: { subtitle: "Sauces, spices & more to enjoy at home", skip: "Skip", continue: "Continue to Payment", added: "Added!" },
  fr: { subtitle: "Sauces, epices et plus a emporter chez vous", skip: "Passer", continue: "Continuer vers le paiement", added: "Ajoute !" },
  th: { subtitle: "ซอส เครื่องเทศ และอื่นๆ สำหรับทานที่บ้าน", skip: "ข้าม", continue: "ดำเนินการชำระเงิน", added: "เพิ่มแล้ว!" },
  ru: { subtitle: "Соусы, специи и многое другое для дома", skip: "Пропустить", continue: "Перейти к оплате", added: "Добавлено!" },
  de: { subtitle: "Saucen, Gewurze und mehr fur zu Hause", skip: "Uberspringen", continue: "Weiter zur Zahlung", added: "Hinzugefugt!" },
  es: { subtitle: "Salsas, especias y mas para disfrutar en casa", skip: "Omitir", continue: "Continuar al pago", added: "Anadido!" },
  ar: { subtitle: "صلصات وبهارات والمزيد للاستمتاع بها في المنزل", skip: "تخطي", continue: "متابعة الدفع", added: "تمت الإضافة!" },
  zh: { subtitle: "酱料、香料等，带回家享用", skip: "跳过", continue: "继续付款", added: "已添加!" },
};

const TakeawayUpsell = ({ isOpen, onClose, onComplete }) => {
  const { t, language } = useLanguage();
  const { addToCart, cart } = useCart();
  const [takeawayItems, setTakeawayItems] = useState([]);
  const [addedItems, setAddedItems] = useState([]);

  const txt = UPSELL_TEXT[language] || UPSELL_TEXT.en;

  useEffect(() => {
    if (isOpen) {
      fetchTakeawayItems();
      setAddedItems([]);
    }
  }, [isOpen]);

  const fetchTakeawayItems = async () => {
    try {
      const response = await axios.get(`${API}/menu/takeaway?lang=${language}`);
      setTakeawayItems(response.data.slice(0, 8));
    } catch (error) {
      console.error('Error fetching takeaway items:', error);
    }
  };

  const handleAddItem = (item) => {
    addToCart(item);
    setAddedItems(prev => [...prev, item.id]);
  };

  const formatPrice = (price) => `฿${price.toLocaleString()}`;

  const isItemInCart = (itemId) => {
    return cart.some(i => i.item_id === itemId) || addedItems.includes(itemId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-[#FAF7F2] rounded-3xl p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 bg-gradient-to-br from-[#0A422D] to-[#083323] text-white">
          <DialogTitle className="text-2xl font-bold flex items-center gap-3">
            <ShoppingBag className="w-7 h-7" />
            {t('takeaway')}
          </DialogTitle>
          <p className="text-white/80 text-sm mt-1">{txt.subtitle}</p>
        </DialogHeader>

        <div className="p-4 max-h-[55vh] overflow-y-auto">
          {takeawayItems.length === 0 ? (
            <div className="text-center py-8 text-[#6E5E52]">
              <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Loading...</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {takeawayItems.map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl p-3 border border-[#E5DCD3] shadow-sm"
                  data-testid={`takeaway-item-${item.id}`}
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-24 object-cover rounded-xl mb-2"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/120x96?text=No+Image'; }}
                  />
                  <h4 className="font-semibold text-sm text-[#2D241D] line-clamp-1">{item.name}</h4>
                  <p className="text-xs text-[#6E5E52] line-clamp-1 mb-2">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-[#E16833] text-sm">{formatPrice(item.price)}</span>
                    {isItemInCart(item.id) ? (
                      <span className="h-8 px-2 rounded-full bg-[#2D6A4F] text-white flex items-center justify-center text-xs gap-1">
                        <Check className="w-3 h-3" /> {txt.added}
                      </span>
                    ) : (
                      <Button
                        onClick={() => handleAddItem(item)}
                        className="w-8 h-8 rounded-full bg-[#E16833] hover:bg-[#C95A2A] p-0"
                        data-testid={`add-takeaway-${item.id}`}
                      >
                        <Plus className="w-4 h-4 text-white" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#E5DCD3] flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 h-12 rounded-full border-[#E5DCD3] text-[#6E5E52]"
            data-testid="takeaway-skip-btn"
          >
            <X className="w-4 h-4 mr-2" />
            {txt.skip}
          </Button>
          <Button
            onClick={onComplete}
            className="flex-1 h-12 btn-primary rounded-full font-semibold"
            data-testid="continue-to-payment"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {txt.continue}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TakeawayUpsell;
